import { Link } from "react-router-dom";
import { PAYOUT_STATUS_LABELS, REFUND_REASON_LABELS } from "@ticketnjoy/types";

import {
  ADMIN_ROLE_LABELS,
  MEMBER_ROLE_LABELS,
  MEMBER_STATUS_LABELS,
  ORDER_STATUS_LABELS,
  ORGANIZER_STATUS_LABELS,
  PAYMENT_PROVIDER_LABELS,
  PAYMENT_QUERY_STATUS_LABELS,
  PLATFORM_ACTION_LABELS,
  dateTime,
} from "@/lib/format";

interface PlatformLogRowProps {
  action: string;
  /** Verilmezse platform işlem etiketi kullanılır (organizatör Aktivite satırları kendi etiketini verir). */
  label?: string;
  actorEmail: string;
  diff: string | null;
  createdAt: string;
  ipAddress?: string | null;
  /** Platform kayıtları sayfasında hedef organizatöre link. */
  organizer?: { id: string; name: string | null } | null;
  /** Bağlamdan zaten belli olan alanlar (ör. sipariş sayfasında sipariş no). */
  hiddenDiffKeys?: string[];
}

export function PlatformLogRow({
  action,
  label,
  actorEmail,
  diff,
  createdAt,
  ipAddress,
  organizer,
  hiddenDiffKeys,
}: PlatformLogRowProps) {
  const lines = describeDiff(diff, new Set(hiddenDiffKeys));
  return (
    <li className="py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{label ?? PLATFORM_ACTION_LABELS[action] ?? action}</span>
        <span className="text-xs text-muted-foreground">{dateTime(createdAt)}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {actorEmail}
        {ipAddress && <> · {ipAddress}</>}
        {organizer && (
          <>
            {" · "}
            <Link to={`/organizers/${organizer.id}`} className="text-foreground hover:underline">
              {organizer.name ?? "Organizatör"}
            </Link>
          </>
        )}
      </div>
      {lines.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

const DIFF_KEY_LABELS: Record<string, string> = {
  status: "Durum",
  reason: "Sebep",
  amount: "Tutar",
  currency: "Para birimi",
  iban: "IBAN",
  availableBefore: "Önceki çekilebilir bakiye",
  overBalance: "Bakiyeyi aşıyor",
  note: "Not",
  paidOn: "Ödeme tarihi",
  bankReference: "Dekont no",
  expiresAt: "Bitiş",
  admin: "Admin",
  displayName: "Ad",
  role: "Rol",
  revokedSessions: "Kapatılan oturum",
  endedSupportSessions: "Kapatılan destek oturumu",
  forgottenTrustedIps: "Unutulan tanınan IP",
  codeSkipped: "Kod sorulmadı (tanınan IP)",
  codeDisabled: "Kod kapalı (test ortamı)",
  eventTitle: "Etkinlik",
  orderNumber: "Sipariş",
  channel: "Kanal",
  ticketCount: "Bilet",
  isPartial: "Kısmi",
  reasonNote: "Açıklama",
  firstName: "Ad",
  lastName: "Soyad",
  email: "E-posta",
  phone: "Telefon",
  sessions: "Kapatılan oturum",
  owner: "Hesap sahibi",
  previousOwnerRole: "Eski sahibin yeni rolü",
  newOwnerPreviousRole: "Yeni sahibin önceki rolü",
  companyName: "Firma adı",
  phoneNumber: "Telefon",
  preferredLocale: "Dil",
  logo: "Logo",
  legalName: "Ünvan",
  taxNumber: "Vergi no",
  taxOffice: "Vergi dairesi",
  billingAddress: "Fatura adresi",
  ibanOwnerName: "Hesap sahibi",
  provider: "Sağlayıcı",
  cardFeeRate: "Kart komisyonu (%)",
  cardFeeRateOverride: "Özel kart komisyonu (%)",
  cardProvider: "Kart POS'u",
  defaultCardProvider: "Varsayılan kart POS'u",
  providerStatus: "Banka kodu",
  bankStatus: "Banka durumu",
  bankAmount: "Bankadaki tutar",
  expectedAmount: "Beklenen tutar",
  previousStatus: "Önceki durum",
  confirmation: "Onay",
  transactionId: "İşlem no",
  providerRefundId: "İade işlem no",
  alreadyRefundedAtBank: "Bankada zaten iade edilmiş",
};

const DATE_TIME_DIFF_KEYS = new Set(["expiresAt"]);

const HIDDEN_DIFF_KEYS = new Set(["payoutId", "adjustmentId", "eventId", "memberId"]);

const VALUE_LABELS: Record<string, string> = {
  email: "E-posta",
  sms: "SMS",
  added: "eklendi",
  changed: "değişti",
  removed: "kaldırıldı",
  platformDefault: "platform varsayılanı",
  providerRate: "POS'un oranı",
  none: "yok",
  provider_query: "banka doğruladı",
  override: "banka doğrulamadan, sebeple",
};

function describeDiff(diff: string | null, hidden: Set<string>): string[] {
  if (!diff) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(diff);
  } catch {
    return [diff];
  }
  if (!parsed || typeof parsed !== "object") return [];
  return describeEntries(parsed as Record<string, unknown>, hidden);
}

/**
 * `changes` (ödeme düzenleme) iç içe gelir; satırları düzleştirilir. jsonb anahtar sırasını
 * korumadığı için hedef admin satırı öne alınır.
 */
function describeEntries(entries: Record<string, unknown>, hidden: Set<string>): string[] {
  const ordered = Object.entries(entries).sort(([a], [b]) => Number(b === "admin") - Number(a === "admin"));
  return ordered.flatMap(([key, value]) => {
    if (HIDDEN_DIFF_KEYS.has(key) || hidden.has(key)) return [];
    if (key === "changes" && value && typeof value === "object") {
      return describeEntries(value as Record<string, unknown>, hidden);
    }
    const label = DIFF_KEY_LABELS[key] ?? key;
    if (DATE_TIME_DIFF_KEYS.has(key) && typeof value === "string") {
      return [`${label}: ${dateTime(value)}`];
    }
    if (value && typeof value === "object" && "oldValue" in value && "newValue" in value) {
      const v = value as { oldValue: unknown; newValue: unknown };
      return [`${label}: ${pretty(v.oldValue)} → ${pretty(v.newValue)}`];
    }
    return [`${label}: ${pretty(value)}`];
  });
}

function pretty(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "evet" : "hayır";
  if (typeof value === "number") return value.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
  if (typeof value === "string") {
    return (
      ORGANIZER_STATUS_LABELS[value as keyof typeof ORGANIZER_STATUS_LABELS] ??
      PAYOUT_STATUS_LABELS[value as keyof typeof PAYOUT_STATUS_LABELS] ??
      ORDER_STATUS_LABELS[value as keyof typeof ORDER_STATUS_LABELS] ??
      REFUND_REASON_LABELS[value as keyof typeof REFUND_REASON_LABELS] ??
      ADMIN_ROLE_LABELS[value as keyof typeof ADMIN_ROLE_LABELS] ??
      MEMBER_ROLE_LABELS[value as keyof typeof MEMBER_ROLE_LABELS] ??
      MEMBER_STATUS_LABELS[value as keyof typeof MEMBER_STATUS_LABELS] ??
      PAYMENT_QUERY_STATUS_LABELS[value as keyof typeof PAYMENT_QUERY_STATUS_LABELS] ??
      PAYMENT_PROVIDER_LABELS[value] ??
      VALUE_LABELS[value] ??
      value
    );
  }
  return JSON.stringify(value);
}
